import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLossReasons } from '@/hooks/useLossReasons';
import { useTeam } from '@/hooks/useTeam';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Loader2 } from 'lucide-react';

interface LossReasonDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (lossReasonId: string, lostByUserId?: string) => void;
  opportunityTitle?: string;
}

export function LossReasonDialog({ open, onClose, onConfirm, opportunityTitle }: LossReasonDialogProps) {
  const { lossReasons, loading, createLossReason } = useLossReasons();
  const { members, loading: loadingMembers } = useTeam();
  const { user } = useAuth();
  const [selectedReasonId, setSelectedReasonId] = useState<string>('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newReasonName, setNewReasonName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const activeMembers = members.filter((member) => member.is_active);
  const currentMemberId =
    activeMembers.find((member) => member.auth_user_id === user?.auth_user_id)?.id ?? '';

  // Pre-selecting the logged-in member is what makes the field actually get filled:
  // confirmation only requires the reason, so an empty select would stay empty
  useEffect(() => {
    if (open) {
      setSelectedMemberId((current) => current || currentMemberId);
    }
  }, [open, currentMemberId]);

  const handleSelectChange = (value: string) => {
    if (value === '__new__') {
      setIsAddingNew(true);
      setSelectedReasonId('');
    } else {
      setSelectedReasonId(value);
      setIsAddingNew(false);
    }
  };

  const handleCreateNewReason = async () => {
    if (!newReasonName.trim()) return;

    setIsCreating(true);
    const newReason = await createLossReason(newReasonName.trim());
    setIsCreating(false);

    if (newReason) {
      setSelectedReasonId(newReason.id);
      setNewReasonName('');
      setIsAddingNew(false);
    }
  };

  const handleConfirm = () => {
    if (selectedReasonId) {
      onConfirm(selectedReasonId, selectedMemberId || undefined);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedReasonId('');
    setSelectedMemberId('');
    setIsAddingNew(false);
    setNewReasonName('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border rounded-2xl max-w-md dark:bg-[#18181B] dark:border-[#3F3F464D]">
        <DialogHeader>
          <DialogTitle className="text-foreground dark:text-white">Motivo da Perda</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {opportunityTitle && (
            <p className="text-sm text-muted-foreground dark:text-neutral-400">
              Informe o motivo da perda para: <span className="text-foreground font-medium dark:text-white">{opportunityTitle}</span>
            </p>
          )}

          <div className="space-y-2">
            <Label className="text-foreground dark:text-white">Selecione o motivo</Label>
            <Select value={selectedReasonId} onValueChange={handleSelectChange} disabled={loading}>
              <SelectTrigger className="w-full bg-white text-foreground border-border dark:bg-neutral-600 dark:text-white dark:border-[#3F3F46]">
                <SelectValue placeholder={loading ? "Carregando..." : "Selecione um motivo"} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {lossReasons.map((reason) => (
                    <SelectItem key={reason.id} value={reason.id}>
                      {reason.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__" className="text-blue-400">
                    <span className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Adicionar novo motivo
                    </span>
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-foreground dark:text-white">Membro da Equipe</Label>
            <Select
              value={selectedMemberId}
              onValueChange={setSelectedMemberId}
              disabled={loadingMembers}
            >
              <SelectTrigger className="w-full bg-white text-foreground border-border dark:bg-neutral-600 dark:text-white dark:border-[#3F3F46]">
                <SelectValue placeholder={loadingMembers ? 'Carregando...' : 'Selecione um membro'} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {activeMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name || member.email}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {isAddingNew && (
            <div className="space-y-2 p-3 bg-[#F8FAFC] rounded-lg border border-border dark:bg-neutral-700/50 dark:border-neutral-600">
              <Label className="text-foreground dark:text-white">Novo motivo de perda</Label>
              <div className="flex gap-2">
                <Input
                  value={newReasonName}
                  onChange={(e) => setNewReasonName(e.target.value)}
                  placeholder="Ex: Preço alto, Concorrente..."
                  className="flex-1 bg-white text-foreground border-border placeholder:text-muted-foreground dark:bg-neutral-600 dark:text-white dark:border-[#3F3F46] dark:placeholder:text-gray-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateNewReason();
                    }
                  }}
                />
                <Button
                  onClick={handleCreateNewReason}
                  disabled={!newReasonName.trim() || isCreating}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-border text-foreground hover:bg-[#F1F5F9] dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-black"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedReasonId}
            className="bg-red-600 hover:bg-red-700"
          >
            Confirmar Perda
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
